// @flow

import * as React from 'react';
import { Box, Flex, useSystem } from 'react-system';
import { Dialog, IconButton, Button } from '@material-ui/core';
import { graphql, useFragment } from 'react-relay';
import * as icons from '../../icons';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { useRouter } from '../../controls/router';
import type { Href } from '../../controls/link';
import { useUserData } from '../../hooks/user-data';
import { AggregatesListing } from './AggregatesListing';
import { getCountryLabel } from './useAggregatesListingsPlaceData';
import type { ListingsParams } from './listingsDescriptor';
import type { aggregatesListingDialog_listing$key } from './__generated__/aggregatesListingDialog_listing.graphql';
import type { aggregatesListingDialog_aggregatesListings$key } from './__generated__/aggregatesListingDialog_aggregatesListings.graphql';

type Props = {|
  listing: ?aggregatesListingDialog_listing$key,
  params?: ListingsParams,
  aggregatesListings?: aggregatesListingDialog_aggregatesListings$key,
  shallowCardsLink: boolean,
  replaceCardsLink: boolean,
  backHref: Href,
  // Used for client only
  onClose?: (e: SyntheticKeyboardEvent<> | SyntheticMouseEvent<>) => void,
|};

const DialogPlaceholderContainer = () => {
  const { colors } = useTheme();

  const placeholderColor = colors.grey100;

  return (
    <Flex flexDirection={'column'} pt={['38px', 0]}>
      <Box
        height={['300px', '339px']}
        css={{
          backgroundColor: placeholderColor,
        }}
      />
      <Flex mt={3}>
        <Box width={[1, 2 / 3]} mx={5}>
          <Box
            css={{
              backgroundColor: placeholderColor,
              height: '40px',
              width: '80%',
            }}
          />
          <Box
            mt={3}
            css={{
              backgroundColor: placeholderColor,
              height: '24px',
              width: '60%',
            }}
          />
          <Box
            mt={3}
            css={{
              backgroundColor: placeholderColor,
              height: '24px',
            }}
          />
          <Box
            mt={3}
            css={{
              backgroundColor: placeholderColor,
              height: '24px',
              width: '70%',
            }}
          />
        </Box>
        <Box width={[1, 1 / 3]} />
      </Flex>
    </Flex>
  );
};

export const AggregatesListingDialog = (props: Props): React.Node => {
  const router = useRouter();
  const { countryCode } = useUserData();

  const listing = useFragment(
    graphql`
      fragment aggregatesListingDialog_listing on AggregatesListing {
        ...AggregatesListing_listing
      }
    `,
    props.listing,
  );
  const aggregatesListings = useFragment(
    graphql`
      fragment aggregatesListingDialog_aggregatesListings on AggregatesListingConnectionConnection {
        totalCount
        filters {
          places {
            label
          }
        }
        ...AggregatesListing_aggregatesListings
      }
    `,
    props.aggregatesListings,
  );

  const { colors, text } = useTheme();
  const { t } = useLocale();
  const { media } = useSystem();
  const ref = React.useRef(null);

  const handleClose = e => {
    if (props.onClose) {
      props.onClose(e);
    } else {
      if (props.backHref != null) {
        router.push(props.backHref, null);
      }
    }
  };

  React.useEffect(() => {
    ref.current?.scrollTo(0, 0);
  }, [listing]);

  // TODO: we need to not use dialog on stanalone page redering and not use on mobile
  // as it breaks carousel scrolling behaviour
  return (
    <Dialog
      // Portals does not support SSR
      disablePortal={true}
      keepMounted={true}
      open={true}
      transitionDuration={0}
      maxWidth={'xl'}
      onClose={handleClose}
      disableEscapeKeyDown={false}
      style={{
        // Force use css value of zIndex, as on mobile we need it to have minimal zIndex
        // as on mobile its not dialog but like a page
        zIndex: null,
      }}
      css={[
        media({
          marginTop: ['55px', 0],
          // 11 on desktop so above menu
          zIndex: [3, 11], // (z-index of header)
        }),
        {
          '.MuiBackdrop-root': media({
            marginTop: ['55px', 0],
          }),
          '.MuiDialog-paperScrollPaper': media({
            maxHeight: '100%',
            borderRadius: 0,
            margin: ['0', '62px'],
            '--scroll-width': 0,
          }),
        },
      ]}
      // Without this at SSR opacity will be set to 0 which cause delay for dialog to appear
      TransitionProps={{
        appear: false,
      }}
      PaperProps={{
        ref,
      }}
    >
      <Box
        css={media({
          width: ['100%', 960, 1138],
          height: '100vh',
        })}
        // prevent closing map popup
        data-invisible-for-click-outside="true"
      >
        {listing != null && (
          <AggregatesListing
            listing={listing}
            shallowCardsLink={props.shallowCardsLink}
            replaceCardsLink={props.replaceCardsLink}
            backHref={props.backHref}
            onClose={props.onClose}
            params={props.params}
            aggregatesListings={aggregatesListings}
          />
        )}
        {props.listing == null && <DialogPlaceholderContainer />}
        {listing == null && props.listing != null && (
          <Flex
            flexDirection={'column'}
            alignItems="center"
            css={{ textAlign: 'center', height: '100%' }}
            justifyContent="center"
          >
            <Box p={2}>
              <icons.OfflineListing size={365} css={{ height: '0.5em' }} />
            </Box>
            <Box css={text.body0}>{t('listingNotAvailable')}</Box>
            <Box css={text.h6} px={3} pt={3} pb={4}>
              {t('otherSearchText', {
                count: aggregatesListings?.totalCount,
                place:
                  // $FlowFixMe[unnecessary-optional-chain]
                  aggregatesListings?.filters.places?.[0]?.label ??
                  getCountryLabel(t, countryCode),
              })}
            </Box>
            <Button
              color="primary"
              onClick={handleClose}
              style={{ width: 'fit-content' }}
            >
              {t('searchAvailableListings')}
            </Button>
          </Flex>
        )}
      </Box>
      <Box
        css={{
          position: 'absolute',
          zIndex: '2',
          right: '-20px',
          top: '5px',
        }}
      >
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          css={{
            position: 'fixed',
            background: 'white',
            '&:hover': {
              background: colors.grey200,
            },
          }}
          onClick={handleClose}
        >
          <icons.Close fill={colors.grey900} size={24} />
        </IconButton>
      </Box>
    </Dialog>
  );
};
